import type ApiClient from '@core/api/client'
import { getMarketFromTimezone } from '@core/utils'
import { useRollbar } from '@rollbar/react'
import { useEffect, useRef } from 'react'

// Base paths that should sync by default
const SYNC_BASE_PATHS = ['/photo-products', '/egifts']

// Paths that should NOT sync (excluded from sync)
const EXCLUDED_PATHS = [
  '/photo-products/landing/buy-now',
  // The Event pages that replace buy-now for the same creatives.
  '/photo-products/event',
  '/photo-products/dpf-landing-us',
  '/photo-products/dpf-landing-jp',
  '/egifts/verify-email',
  '/egifts/celebrate/view',
  '/egifts/login',
  '/egifts/reset-password',
  '/referral/redeem',
]

/**
 * Determines if a given pathname should trigger market sync
 */
function shouldSyncPath(pathname: string): boolean {
  const isExcluded = EXCLUDED_PATHS.some(
    (excluded) => pathname === excluded || pathname.startsWith(excluded + '/')
  )

  if (isExcluded) {
    return false
  }

  // Check if pathname starts with any base path
  const matchesBasePath = SYNC_BASE_PATHS.some(
    (base) => pathname === base || pathname.startsWith(base + '/')
  )

  return matchesBasePath
}

/**
 * Hook that syncs userMarket in the cookie with market calculated from timezone.
 * Only syncs for /photo-products/* and /egifts/* paths, excluding `EXCLUDED_PATHS`.
 * Syncs only once per component mount, if the initial route matches; does not re-sync on subsequent route changes.
 */
export function useMarketSync(api: ApiClient, pathname: string) {
  const hasSynced = useRef(false)
  const rollbar = useRollbar()

  useEffect(() => {
    // Only sync once per component mount
    if (hasSynced.current) {
      return
    }

    // Check if the current path should sync
    if (!shouldSyncPath(pathname)) {
      return
    }

    hasSynced.current = true

    const calculatedMarket = getMarketFromTimezone()

    // Sync the calculated market (fire and forget)
    api.updateUserMarket(calculatedMarket).catch((error) => {
      rollbar.info('useMarketSync failed to update userMarket', error, {
        market: calculatedMarket,
      })
    })
  }, [api, rollbar, pathname])
}
